import React, { useEffect, useState } from "react";
import {
  FaBell,
  FaEye,
  FaFilter,
  FaSearch,
  FaUpload,
} from "react-icons/fa";
import "../styles/TransactionsPage.css";
import AppDropdown from "../components/AppDropdown";
import { getOrgId, getUserData } from "../utils/authStorage";
import { downloadCsv } from "../utils/download";
import { useToast } from "../context/ToastContext";
import {
  getOrgFailedPayments,
  getOrgMerchantDistribution,
  getOrgRecentTransactions,
  getOrgRevenueTrend,
  getOrgSystemAlerts,
  getOrgTransactionSummary,
} from "../api/api";
import useSidebarAction from "../hooks/useSidebarAction";

const pick = (res) => res?.data ?? res ?? null;
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const asArray = (raw) =>
  (Array.isArray(raw) && raw) ||
  (Array.isArray(raw?.data) && raw.data) ||
  (Array.isArray(raw?.series) && raw.series) ||
  (Array.isArray(raw?.items) && raw.items) ||
  (Array.isArray(raw?.transactions) && raw.transactions) ||
  (Array.isArray(raw?.alerts) && raw.alerts) ||
  (Array.isArray(raw?.categories) && raw.categories) ||
  [];
const formatRs = (v) => {
  const n = num(v);
  if (n >= 1000000) return `Rs ${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `Rs ${(n / 1000).toFixed(1)}k`;
  return `Rs ${n.toLocaleString()}`;
};
const normalizeTxRow = (t, idx) => ({
  id: t.transactionId ?? t.txnId ?? t.txId ?? t.id ?? `#${idx + 1}`,
  merchant: t.merchant ?? t.merchantName ?? "—",
  agent: t.agent ?? t.agentName ?? t.agentId ?? "—",
  type: t.qrType ?? t.type ?? "—",
  amount: formatRs(t.amount ?? t.volume),
  date: t.date ?? t.time ?? t.createdAt ?? "—",
  status: String(t.status ?? "Pending"),
  settlement: t.settlement ?? t.settlementStatus ?? "—",
});

const DONUT_COLORS = ["teal", "mint", "slate", "amber", "violet"];
const DATE_RANGES = [
  { value: "today", label: "Today" },
  { value: "last7days", label: "Last 7 Days" },
  { value: "last30days", label: "Last 30 Days" },
  { value: "last90days", label: "Last 90 Days" },
];

const Transactions = ({ isSidebarOpen }) => {
  const userData = getUserData();
  const user = userData?.user || userData?.data || userData || {};
  const userType = String(user?.user_type || user?.type || "").toLowerCase();
  const isOrgAdmin = userType === "org" || userType === "org-admin" || userType === "organizationadmin";
  const orgId = getOrgId() || user?.profile?.orgId || user?.orgId || user?.organizationId || null;

  /* ── org-admin filters ── */
  const [dateRange, setDateRange] = useState("last7days");
  const [status, setStatus] = useState("all");
  const [qrType, setQrType] = useState("both");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  /* ── org-admin data ── */
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [trendSeries, setTrendSeries] = useState([]);
  const [distribution, setDistribution] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [failedPayments, setFailedPayments] = useState([]);
  const [txLoading, setTxLoading] = useState(true);
  const [txRows, setTxRows] = useState([]);
  const [txMeta, setTxMeta] = useState({ total: 0, totalPages: 1 });
  const [exportingCsv, setExportingCsv] = useState(false);
  const { showError, showInfo } = useToast();

  /* Export every transaction matching the current filters (not just this page) */
  const handleExportAllCsv = async () => {
    if (!orgId || exportingCsv) return;
    setExportingCsv(true);
    try {
      const raw = pick(
        await getOrgRecentTransactions({
          orgId,
          page: 1,
          limit: 1000,
          status,
          qrType,
          dateRange,
          search,
        })
      );
      const rows = asArray(raw).map(normalizeTxRow);
      if (!rows.length) {
        showInfo("Export", "There are no transactions to export", 2200);
        return;
      }
      downloadCsv(
        ["Transaction ID", "Merchant", "Agent", "Type", "Amount", "Date", "Status", "Settlement"],
        rows.map((r) => [r.id, r.merchant, r.agent, r.type, r.amount, r.date, r.status, r.settlement]),
        "transactions.csv"
      );
    } catch (err) {
      console.error("Error exporting transactions:", err);
      showError("Export", err?.response?.data?.message || "Failed to export transactions", 3000);
    } finally {
      setExportingCsv(false);
    }
  };

  /* Sidebar dropdown action → run the same "Export All CSV" */
  useSidebarAction({ export: handleExportAllCsv });

  /* debounce quick search */
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  /* overview: summary, revenue trend, distribution, alerts, failed payments */
  useEffect(() => {
    if (!isOrgAdmin || !orgId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const fetchOverview = async () => {
      setLoading(true);
      const results = await Promise.allSettled([
        getOrgTransactionSummary(orgId, dateRange),
        getOrgRevenueTrend(orgId, dateRange),
        getOrgMerchantDistribution(orgId),
        getOrgSystemAlerts(orgId),
        getOrgFailedPayments(orgId, 5),
      ]);
      if (cancelled) return;
      const val = (i) => (results[i].status === "fulfilled" ? pick(results[i].value) : null);

      setSummary(val(0));

      setTrendSeries(
        asArray(val(1)).map((d) => ({
          label: String(d?.label ?? d?.day ?? d?.date ?? d?.period ?? ""),
          amount: num(d?.amount ?? d?.revenue ?? d?.total ?? d?.value),
        }))
      );

      setDistribution(val(2));

      setAlerts(
        asArray(val(3)).map((a) => ({
          title: a.title ?? a.type ?? a.name ?? "Alert",
          text: a.text ?? a.message ?? a.description ?? "",
        }))
      );

      setFailedPayments(
        asArray(val(4)).map((f) => ({
          id: f.code ?? f.errorCode ?? f.id ?? f.transactionId ?? "—",
          text: f.text ?? f.message ?? `${formatRs(f.amount)} - ${f.merchant ?? f.merchantName ?? "—"}`,
          when: f.when ?? f.time ?? f.createdAt ?? "",
        }))
      );

      setLoading(false);
    };

    fetchOverview().catch(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isOrgAdmin, orgId, dateRange]);

  /* recent transactions: paginated + filters */
  useEffect(() => {
    if (!isOrgAdmin || !orgId) {
      setTxLoading(false);
      return;
    }
    let cancelled = false;

    const fetchTx = async () => {
      setTxLoading(true);
      try {
        const raw = pick(
          await getOrgRecentTransactions({
            orgId,
            page,
            limit: 10,
            status,
            qrType,
            dateRange,
            search,
          })
        );
        if (cancelled) return;

        setTxRows(asArray(raw).map(normalizeTxRow));

        const pg = raw?.pagination ?? raw ?? {};
        setTxMeta({
          total: num(pg.total ?? pg.totalRecords ?? pg.totalItems ?? pg.count),
          totalPages: num(pg.totalPages ?? pg.pages) || 1,
        });
      } catch {
        if (!cancelled) {
          setTxRows([]);
          setTxMeta({ total: 0, totalPages: 1 });
        }
      }
      if (!cancelled) setTxLoading(false);
    };

    fetchTx();
    return () => {
      cancelled = true;
    };
  }, [isOrgAdmin, orgId, page, status, qrType, dateRange, search]);

  const s = summary || {};
  const stats = [
    { label: "Total Transactions", value: loading ? "…" : num(s.totalTransactions ?? s.transactions ?? s.total).toLocaleString(), trend: "", tone: "teal" },
    { label: "Revenue", value: loading ? "…" : formatRs(s.revenue ?? s.totalRevenue ?? s.volume ?? s.totalVolume), trend: "", tone: "mint" },
    { label: "Success Rate", value: loading ? "…" : `${num(s.successRate ?? s.success_rate ?? s.successPercent)}%`, trend: "", tone: "green" },
    { label: "Pending Settlements", value: loading ? "…" : num(s.pendingSettlements ?? s.pending ?? s.pendingCount).toLocaleString(), trend: "", tone: "amber" },
    { label: "Avg. Trans. Size", value: loading ? "…" : formatRs(s.avgTransactionSize ?? s.averageTransactionSize ?? s.avgTransaction ?? s.avgSize), trend: "", tone: "slate" },
    { label: "QR Conversion", value: loading ? "…" : `${num(s.qrConversion ?? s.qrConversionRate)}%`, trend: "", tone: "violet" },
  ];

  const maxTrend = Math.max(...trendSeries.map((d) => d.amount), 0);
  const trendBars = trendSeries.map((d) => ({
    label: d.label,
    amount: d.amount,
    h: maxTrend ? Math.max((d.amount / maxTrend) * 100, 2) : 2,
  }));

  const dist = distribution || {};
  const distCategories = asArray(dist.categories ?? dist).map((c, idx) => ({
    name: c.name ?? c.category ?? c.label ?? "—",
    percent: num(c.percent ?? c.share ?? c.percentage ?? c.value),
    color: DONUT_COLORS[idx % DONUT_COLORS.length],
  }));
  const activeMerchants = num(dist.activeMerchants ?? dist.total ?? dist.totalMerchants ?? dist.count);

  /* No settlements API yet — keep empty until the backend endpoint exists */
  const settlements = [];

  if (!isOrgAdmin) {
    return (
      <div className={`msd-container ${isSidebarOpen ? "shifted" : ""}`}>
        <div className="msd-card">
          <div className="msd-card__head">
            <div>
              <div className="msd-card__title">Transactions</div>
              <div className="msd-card__subtitle">Search and monitor transaction activity</div>
            </div>
          </div>
          <div className="msd-card__body">
            <p className="msd-muted">Coming soon</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`tl-page ${isSidebarOpen ? "shifted" : ""}`}>
      <div className="tl-topbar">
        <div className="tl-search">
          <FaSearch />
          <input type="text" placeholder="Search reports..." />
        </div>
        <div className="tl-topbar-actions">
          <button type="button" className="tl-btn tl-btn--primary" onClick={handleExportAllCsv} disabled={exportingCsv}>
            <FaUpload /> {exportingCsv ? "Exporting…" : "Export All CSV"}
          </button>
        </div>
      </div>

      <div className="tl-header">
        <h2>Transaction Ledger</h2>
        <p>Real-time overview of global flows and settlements</p>
      </div>

      <div className="tl-stats">
        {stats.map((s) => (
          <article className={`tl-stat tl-stat--${s.tone}`} key={s.label}>
            <span>{s.label}</span>
            <strong>{s.value}</strong>
            <small>{s.trend}</small>
          </article>
        ))}
      </div>

      <div className="tl-filters">
        <div className="tl-filter"><label>Filters</label>All Merchants</div>
        <div className="tl-filter">All Agents</div>
        <AppDropdown
          className="appdd--block"
          options={DATE_RANGES}
          value={dateRange}
          onChange={(v) => { setDateRange(v); setPage(1); }}
        />
        <AppDropdown
          className="appdd--block"
          options={[
            { value: "all", label: "Status: All" },
            { value: "success", label: "Status: Success" },
            { value: "pending", label: "Status: Pending" },
            { value: "failed", label: "Status: Failed" },
          ]}
          value={status}
          onChange={(v) => { setStatus(v); setPage(1); }}
        />
        <AppDropdown
          className="appdd--block"
          options={[
            { value: "both", label: "QR: Both" },
            { value: "dynamic", label: "QR: Dynamic" },
            { value: "static", label: "QR: Static" },
          ]}
          value={qrType}
          onChange={(v) => { setQrType(v); setPage(1); }}
        />
        <div className="tl-filter tl-filter--search">
          <FaSearch />
          <input
            type="text"
            className="tl-filter-input"
            placeholder="Quick find..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      <div className="tl-grid">
        <section className="tl-main">
          <div className="tl-duo">
            <article className="tl-card">
              <div className="tl-card-head">
                <h3>Revenue Trend</h3>
                <small>Daily Total (EUR)</small>
              </div>
              <div className="tl-trendbars">
                {trendBars.length ? (
                  trendBars.map((b, i) => (
                    <span key={i} style={{ height: `${b.h}%` }} title={`${b.label}: ${formatRs(b.amount)}`} />
                  ))
                ) : (
                  <p className="tl-empty">{loading ? "Loading..." : "No data"}</p>
                )}
              </div>
            </article>

            <article className="tl-card">
              <div className="tl-card-head">
                <h3>Merchant Distribution</h3>
                <small>By Category</small>
              </div>
              <div className="tl-donut-wrap">
                <div className="tl-donut"><span>{activeMerchants}</span><small>active</small></div>
                <ul>
                  {distCategories.length ? (
                    distCategories.map((c) => (
                      <li key={c.name}><i className={c.color} />{c.name} ({c.percent}%)</li>
                    ))
                  ) : (
                    <li>{loading ? "Loading..." : "No data"}</li>
                  )}
                </ul>
              </div>
            </article>
          </div>

          <article className="tl-card tl-table-card">
            <div className="tl-card-head">
              <h3>Recent Transactions</h3>
              <button type="button">View All Activity</button>
            </div>

            <table className="tl-table">
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Merchant</th>
                  <th>Agent</th>
                  <th>QR Type</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Settlement</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {txLoading ? (
                  <tr><td colSpan={9} className="tl-empty">Loading transactions...</td></tr>
                ) : txRows.length ? (
                  txRows.map((row, idx) => (
                    <tr key={`${row.id}-${idx}`}>
                      <td>{row.id}</td>
                      <td>{row.merchant}</td>
                      <td>{row.agent}</td>
                      <td><span className="tl-chip">{row.type}</span></td>
                      <td>{row.amount}</td>
                      <td>{row.date}</td>
                      <td><span className={`tl-status tl-status--${row.status.toLowerCase()}`}>{row.status}</span></td>
                      <td><span className="tl-settlement">{row.settlement}</span></td>
                      <td className="tl-actions"><FaEye /><FaUpload /><FaFilter /></td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={9} className="tl-empty">No transactions found</td></tr>
                )}
              </tbody>
            </table>

            <div className="tl-table-footer">
              <span>
                {txMeta.total
                  ? `Showing page ${page} of ${txMeta.totalPages} (${txMeta.total.toLocaleString()} entries)`
                  : `Page ${page} of ${txMeta.totalPages}`}
              </span>
              <div className="tl-pages">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>&lt;</button>
                <span className="tl-page-current">{page}</span>
                <button type="button" disabled={page >= txMeta.totalPages} onClick={() => setPage((p) => p + 1)}>&gt;</button>
              </div>
            </div>
          </article>
        </section>

        <aside className="tl-side">
          <article className="tl-card tl-side-card">
            <div className="tl-side-title"><FaBell /> System Alerts</div>
            <div className="tl-alerts">
              {alerts.length ? (
                alerts.map((a, idx) => (
                  <div key={`${a.title}-${idx}`}>
                    <strong>{a.title}</strong>
                    <p>{a.text}</p>
                  </div>
                ))
              ) : (
                <p className="tl-empty">{loading ? "Loading..." : "No alerts"}</p>
              )}
            </div>
          </article>

          <article className="tl-card tl-side-card">
            <div className="tl-side-title">Failed Payments</div>
            <div className="tl-fails">
              {failedPayments.length ? (
                failedPayments.map((f, idx) => (
                  <div key={`${f.id}-${idx}`}>
                    <strong>{f.id}</strong>
                    <p>{f.text}</p>
                    <small>{f.when}</small>
                  </div>
                ))
              ) : (
                <p className="tl-empty">{loading ? "Loading..." : "No failed payments"}</p>
              )}
            </div>
          </article>

          <article className="tl-card tl-side-card">
            <div className="tl-side-title">Recent Settlements</div>
            {settlements.length ? (
              <>
                <ul className="tl-settlements">
                  {settlements.map((s) => (
                    <li key={s.batch}><span>{s.batch}</span><strong>{s.amount}</strong></li>
                  ))}
                </ul>
                <div className="tl-next-pay">
                  <small>Next Payout</small>
                  <strong>0%</strong>
                </div>
              </>
            ) : (
              <p className="tl-empty">No settlements yet</p>
            )}
          </article>
        </aside>
      </div>
    </div>
  );
};

export default Transactions;
